import { useContext, useEffect, useRef, useState } from 'react';

import { HubConnection, HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import { getAuth } from 'firebase/auth';

import { AuthContext } from '@/contexts';
import { webSocketApiUrl } from '@/helpers';
import i18n from '@/i18n';
import { useGetActiveServiceMessage } from '@/services/serviceMessage.service';

export const GlobalServiceMessage = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { data: activeServiceMessageData, refetch: refetchActiveServiceMessage } =
    useGetActiveServiceMessage(isAuthenticated);
  const refetchRef = useRef(refetchActiveServiceMessage);
  refetchRef.current = refetchActiveServiceMessage;
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(window.innerWidth >= 1200);

  useEffect(() => {
    const onResize = () => {
      setIsLargeScreen(window.innerWidth >= 1200);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${webSocketApiUrl()}/notificationHub`, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();

    connection.on('serviceMessageChanged', () => {
      if (!cancelled && getAuth().currentUser) {
        refetchRef.current();
      }
    });

    const startPromise = connection.start().catch(err => console.log('err', err));

    return () => {
      cancelled = true;
      connection.off('serviceMessageChanged');
      startPromise.finally(() => {
        connection.stop().catch(err => console.log('err', err));
      });
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleLanguageChanged = () => {
      if (getAuth().currentUser) {
        refetchActiveServiceMessage();
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [isAuthenticated, refetchActiveServiceMessage]);

  const serviceMessage = activeServiceMessageData?.data?.message;

  if (!isAuthenticated || !serviceMessage) {
    return null;
  }

  return (
    <div
      className='pointer-events-none fixed inset-x-0 z-[9999] flex h-8 max-h-8 items-center justify-center overflow-hidden px-3 text-center text-white'
      style={{
        backgroundColor: activeServiceMessageData?.data?.backgroundColor || '#111827',
        top: isLargeScreen ? 0 : 'auto',
        bottom: isLargeScreen ? 'auto' : 0
      }}>
      <span className='w-full truncate'>{serviceMessage}</span>
    </div>
  );
};
